import { useEffect, useState } from 'react'
import { useAuthStore } from './stores/authStore'
import LoginPage from './pages/LoginPage'
import DashboardLayout from './components/DashboardLayout'
import PortfolioCommandCenter from './pages/PortfolioCommandCenter'
import AppOverview from './pages/AppOverview'
import AppAnalyticsPage from './pages/AppAnalyticsPage'
import Settings from './pages/Settings'
import SDKDocs from './pages/SDKDocs'
import type { Page } from './types/navigation'
import { isExploreParam } from './utils/exploreFilters'
import './App.css'

const DEFAULT_PAGE: Page = 'portfolio'

function isAuthCallbackPath(): boolean {
  return window.location.pathname.startsWith('/auth/')
}

function readRouteState(): { page: Page; appId: string | null } {
  const path = window.location.pathname.replace(/\/+$/, '') || '/'
  const query = new URLSearchParams(window.location.search)

  if (path === '/' && query.has('view')) {
    const legacyView = query.get('view')
    const legacyAppId = query.get('appId')
    if (legacyView === 'portfolio-map') return { page: 'portfolio', appId: null }
    if (legacyView === 'app-overview') return { page: 'apps', appId: null }
    if (legacyView === 'app-detail' && legacyAppId) return { page: 'app-detail', appId: legacyAppId }
    if (legacyView === 'settings') return { page: 'settings', appId: legacyAppId }
    if (legacyView === 'sdk-docs') return { page: 'sdk-docs', appId: null }
  }

  if (path === '/' || path === '/portfolio') return { page: 'portfolio', appId: null }
  if (path === '/apps') return { page: 'apps', appId: null }
  if (path === '/settings') return { page: 'settings', appId: query.get('appId') }
  if (path === '/docs/sdk') return { page: 'sdk-docs', appId: null }

  const appMatch = path.match(/^\/apps\/([^/]+)(?:\/([^/]+))?$/)
  if (appMatch) {
    const appId = decodeURIComponent(appMatch[1])
    const section = appMatch[2] ?? 'overview'
    const pageBySection: Record<string, Page> = {
      overview: 'app-detail',
      live: 'app-live',
      breakdowns: 'app-breakdowns',
      explore: 'app-explore',
      setup: 'app-setup',
    }
    return { page: pageBySection[section] ?? 'app-detail', appId }
  }

  return { page: DEFAULT_PAGE, appId: null }
}

function writeRouteState(page: Page, appId: string | null, mode: 'push' | 'replace' = 'push') {
  const url = new URL(window.location.href)
  const appPath = appId ? `/apps/${encodeURIComponent(appId)}` : '/apps'
  const paths: Record<Page, string> = {
    portfolio: '/portfolio',
    apps: '/apps',
    'app-overview': '/apps',
    'app-detail': `${appPath}/overview`,
    'app-live': `${appPath}/live`,
    'app-breakdowns': `${appPath}/breakdowns`,
    'app-explore': `${appPath}/explore`,
    'app-setup': `${appPath}/setup`,
    settings: '/settings',
    'sdk-docs': '/docs/sdk',
  }

  // The Explore tab stores its filter and column state in the query string.
  // This function runs on mount as well as on navigation, so clearing the
  // search unconditionally would wipe the filters out of a shared or reloaded
  // URL before Explore ever reads them.
  // Explore filters are multi-valued (?platform=ios&platform=tvos), so every
  // entry is carried over, not just the first value per key.
  const preserved: Array<[string, string]> =
    page === 'app-explore'
      ? [...new URLSearchParams(window.location.search).entries()].filter(([key]) => isExploreParam(key))
      : []

  url.pathname = paths[page] ?? '/portfolio'
  url.search = ''
  for (const [key, value] of preserved) url.searchParams.append(key, value)
  if (page === 'settings' && appId) url.searchParams.set('appId', appId)

  window.history[mode === 'push' ? 'pushState' : 'replaceState']({ page, appId }, '', url)
}

/**
 * If this is an Apple OAuth callback destined for a localhost dev server,
 * forward immediately before any auth/routing logic runs. This ensures the
 * relay works even when the user is already authenticated on the production
 * site (which would otherwise skip LoginPage entirely).
 */
function checkAppleDevRelay(): boolean {
  if (window.location.pathname !== '/auth/apple/callback') return false
  const query = new URLSearchParams(window.location.search)
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  const payload = hash.toString().length > 0 ? hash : query
  const returnedState = payload.get('state')
  if (!returnedState) return false
  try {
    const parsed = JSON.parse(returnedState) as { s?: string; dev?: string }
    const devOrigin = parsed.dev
    const isOnProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
    if (devOrigin && isOnProduction) {
      const fwd = new URLSearchParams()
      const code = payload.get('code')
      const idToken = payload.get('id_token')
      if (code) fwd.set('code', code)
      if (idToken) fwd.set('id_token', idToken)
      fwd.set('state', returnedState)
      window.location.replace(`${devOrigin}/auth/apple/callback?${fwd.toString()}`)
      return true
    }
  } catch {
    // Plain string state — not a dev relay
  }
  return false
}

function App() {
  // Handle Apple dev relay before any auth/routing — must run before checkAuth
  // so it fires even when the user is already logged in on the production site.
  const [isRelayingAppleDevCallback] = useState(() => checkAppleDevRelay())
  const [isInitialAuthCallback] = useState(() => isAuthCallbackPath())
  const { isAuthenticated, checkAuth } = useAuthStore()
  const [currentPage, setCurrentPage] = useState<Page>(() => readRouteState().page)
  const [selectedAppId, setSelectedAppId] = useState<string | null>(() => readRouteState().appId)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (isRelayingAppleDevCallback) return

    // Check for existing session on mount
    const init = async () => {
      await checkAuth()
      setIsLoading(false)
    }
    init()
  }, [checkAuth, isRelayingAppleDevCallback])

  useEffect(() => {
    const handlePopState = () => {
      const route = readRouteState()
      setCurrentPage(route.page)
      setSelectedAppId(route.appId)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  useEffect(() => {
    if (!isRelayingAppleDevCallback && !isInitialAuthCallback) {
      writeRouteState(currentPage, selectedAppId, 'replace')
    }
  }, [currentPage, selectedAppId, isRelayingAppleDevCallback, isInitialAuthCallback])

  const navigateTo = (page: Page, appId: string | null = null, mode: 'push' | 'replace' = 'push') => {
    setCurrentPage(page)
    setSelectedAppId(appId)
    writeRouteState(page, appId, mode)
  }

  if (isRelayingAppleDevCallback) return null

  if (isInitialAuthCallback && !isAuthenticated) {
    return <LoginPage />
  }

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gray-950">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
          <p className="mt-4 text-gray-400">Loading AppStats...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <LoginPage />
  }

  const handleNavigateToApp = (appId: string) => navigateTo('app-detail', appId)

  const handleBackFromAppDetail = () => {
    navigateTo('apps')
  }

  const handleNavigateToSettings = (appId: string) => {
    navigateTo('settings', appId)
  }

  const handleNavigate = (page: Page) => {
    const appScopedPages: Page[] = ['app-detail', 'app-live', 'app-breakdowns', 'app-explore', 'app-setup']
    navigateTo(page, page === 'settings' || appScopedPages.includes(page) ? selectedAppId : null)
  }

  const handleNavigateToSDKDocs = () => {
    navigateTo('sdk-docs')
  }

  const handleBackFromSDKDocs = () => {
    navigateTo('apps')
  }

  return (
    <DashboardLayout currentPage={currentPage} selectedAppId={selectedAppId} onNavigate={handleNavigate}>
      {currentPage === 'portfolio' && <PortfolioCommandCenter onSelectApp={handleNavigateToApp} />}
      {(currentPage === 'apps' || currentPage === 'app-overview') && (
        <AppOverview 
          onSelectApp={handleNavigateToApp}
          onNavigateToSettings={handleNavigateToSettings}
        />
      )}
      {['app-detail', 'app-live', 'app-breakdowns', 'app-explore', 'app-setup'].includes(currentPage) && selectedAppId && (
        <AppAnalyticsPage
          appId={selectedAppId}
          section={currentPage}
          onBack={handleBackFromAppDetail}
          onNavigate={(page) => navigateTo(page, selectedAppId)}
          onNavigateToSDKDocs={handleNavigateToSDKDocs}
          onNavigateToSettings={() => handleNavigateToSettings(selectedAppId)}
        />
      )}
      {currentPage === 'settings' && <Settings selectedAppId={selectedAppId || undefined} />}
      {currentPage === 'sdk-docs' && <SDKDocs onBack={handleBackFromSDKDocs} />}
    </DashboardLayout>
  )
}

export default App
